/**
 * Task Timing Panel
 * Displays actual vs estimated task completion times
 */

import { useState, useEffect } from 'react';
import { Card } from '../ui';
import { Clock, TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { getRouteTaskTimings, type RouteTaskTimings, type TaskTimingDetail } from '../../services/routes.api';
import { cn } from '../../utils/cn';

interface TaskTimingPanelProps {
  routeId: string;
}

export function TaskTimingPanel({ routeId }: TaskTimingPanelProps) {
  const [timingData, setTimingData] = useState<RouteTaskTimings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTimings() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getRouteTaskTimings(routeId);
        setTimingData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch timings');
      } finally {
        setIsLoading(false);
      }
    }

    fetchTimings();
  }, [routeId]);

  const formatDuration = (minutes: number | null) => {
    if (minutes === null) return 'N/A';
    if (minutes < 1) return `${Math.round(minutes * 60)}s`;
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getVarianceColor = (variancePercent: number | null) => {
    if (variancePercent === null) return 'text-gray-500';
    if (variancePercent < -10) return 'text-green-600'; // Faster than estimated
    if (variancePercent > 10) return 'text-red-600'; // Slower than estimated
    return 'text-yellow-600'; // Within 10%
  };

  const getVarianceIcon = (variancePercent: number | null) => {
    if (variancePercent === null) return <Minus className="h-4 w-4" />;
    if (variancePercent < -10) return <TrendingDown className="h-4 w-4" />;
    if (variancePercent > 10) return <TrendingUp className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
          <span className="ml-2 text-gray-600">Loading timing data...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      </Card>
    );
  }

  if (!timingData) {
    return null;
  }

  const hasCompletedTasks = timingData.completed_tasks > 0;

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-purple-500" />
          <h3 className="font-semibold text-gray-900">Route Timing Summary</h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Completed Tasks</p>
            <p className="text-2xl font-bold text-gray-900">
              {timingData.completed_tasks} / {timingData.total_tasks}
            </p>
          </div>

          {hasCompletedTasks && (
            <>
              <div>
                <p className="text-sm text-gray-500">Total Actual Time</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatDuration(timingData.total_actual_duration_min)}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Total Estimated Time</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatDuration(timingData.total_estimated_duration_min)}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Variance</p>
                <div className="flex items-center gap-2">
                  <p className={cn('text-2xl font-bold', getVarianceColor(timingData.variance_percent))}>
                    {timingData.variance_percent !== null 
                      ? `${timingData.variance_percent > 0 ? '+' : ''}${timingData.variance_percent.toFixed(1)}%`
                      : 'N/A'}
                  </p>
                  <span className={getVarianceColor(timingData.variance_percent)}>
                    {getVarianceIcon(timingData.variance_percent)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {timingData.variance_min !== null 
                    ? `${timingData.variance_min > 0 ? '+' : ''}${formatDuration(timingData.variance_min)}`
                    : ''}
                </p>
              </div>
            </>
          )}
        </div>

        {!hasCompletedTasks && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              No completed tasks yet. Timing data will appear as tasks are completed.
            </p>
          </div>
        )}
      </Card>

      {/* Per-Task Breakdown */}
      <Card className="p-4">
        <h3 className="font-semibold text-gray-900 mb-4">Task-by-Task Breakdown</h3>
        
        <div className="space-y-3">
          {timingData.tasks.map((task) => (
            <TaskTimingRow key={task.task_uid} task={task} />
          ))}
        </div>
      </Card>
    </div>
  );
}

interface TaskTimingRowProps {
  task: TaskTimingDetail;
}

function TaskTimingRow({ task }: TaskTimingRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDuration = (minutes: number | null) => {
    if (minutes === null) return 'N/A';
    if (minutes < 1) return `${Math.round(minutes * 60)}s`;
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: 'Asia/Baghdad',
      });
    } catch {
      return 'N/A';
    }
  };

  const getVarianceColor = (variancePercent: number | null) => {
    if (variancePercent === null) return 'text-gray-500';
    if (variancePercent < -10) return 'text-green-600';
    if (variancePercent > 10) return 'text-red-600';
    return 'text-yellow-600';
  };

  const getVarianceBadgeColor = (variancePercent: number | null) => {
    if (variancePercent === null) return 'bg-gray-100 text-gray-700';
    if (variancePercent < -10) return 'bg-green-100 text-green-700';
    if (variancePercent > 10) return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  const isCompleted = task.actual?.done_at !== null;
  const isCancelled = task.actual?.cancelled_at !== null;

  // Calculate estimated driving time from route stop data
  const estimatedDrivingTime = task.estimated.total_time_min - task.estimated.service_time_min;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500">#{task.sequence + 1}</span>
            <div className="text-left">
              <p className="font-medium text-gray-900">{task.merchant_name || 'Unknown'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isCompleted && task.comparison && (
              <span className={cn(
                'px-2 py-1 text-xs font-semibold rounded',
                getVarianceBadgeColor(task.comparison.total_variance_percent)
              )}>
                {task.comparison.total_variance_percent !== null 
                  ? `${task.comparison.total_variance_percent > 0 ? '+' : ''}${task.comparison.total_variance_percent.toFixed(0)}%`
                  : 'N/A'}
              </span>
            )}

            {isCancelled && (
              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                Cancelled
              </span>
            )}

            {!isCompleted && !isCancelled && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                In Progress
              </span>
            )}

            {isCompleted && (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            )}
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="space-y-4">
            {/* Arrival Time Comparison */}
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Arrival Time</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Estimated</p>
                  <p className="text-lg font-semibold text-gray-900">{formatTime(task.estimated.arrival_time)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Actual</p>
                  <p className="text-lg font-semibold text-gray-900">{formatTime(task.actual?.arrived_at ?? null)}</p>
                </div>
              </div>
            </div>

            {/* Driving Time Comparison */}
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Driving Time</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Estimated</p>
                  <p className="text-lg font-semibold text-gray-900">{formatDuration(estimatedDrivingTime)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Actual</p>
                  <p className="text-lg font-semibold text-gray-900">{formatDuration(task.actual?.driving_duration_min ?? null)}</p>
                  {task.actual?.driving_duration_min !== null && task.actual && estimatedDrivingTime > 0 && (
                    <p className={cn('text-xs font-medium mt-1', getVarianceColor(
                      ((task.actual.driving_duration_min - estimatedDrivingTime) / estimatedDrivingTime) * 100
                    ))}>
                      {((task.actual.driving_duration_min - estimatedDrivingTime) / estimatedDrivingTime * 100).toFixed(1)}%
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Service Time Comparison */}
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Service Time</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Estimated</p>
                  <p className="text-lg font-semibold text-gray-900">{formatDuration(task.estimated.service_time_min)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Actual</p>
                  <p className="text-lg font-semibold text-gray-900">{formatDuration(task.actual?.service_duration_min ?? null)}</p>
                  {task.comparison?.service_variance_percent !== null && task.comparison && (
                    <p className={cn('text-xs font-medium mt-1', getVarianceColor(task.comparison.service_variance_percent))}>
                      {task.comparison.service_variance_percent > 0 ? '+' : ''}{task.comparison.service_variance_percent.toFixed(1)}%
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Detailed Timeline */}
            {task.actual && (
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Detailed Timeline</h4>
                <div className="space-y-2 text-sm">
                  {task.actual.assigned_at && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Assigned</span>
                      <span className="font-medium text-gray-900">{formatTime(task.actual.assigned_at)}</span>
                    </div>
                  )}
                  {task.actual.driving_at && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Started Driving</span>
                      <span className="font-medium text-gray-900">{formatTime(task.actual.driving_at)}</span>
                    </div>
                  )}
                  {task.actual.arrived_at && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Arrived</span>
                      <span className="font-medium text-gray-900">{formatTime(task.actual.arrived_at)}</span>
                    </div>
                  )}
                  {task.actual.in_progress_at && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Started Service</span>
                      <span className="font-medium text-gray-900">{formatTime(task.actual.in_progress_at)}</span>
                    </div>
                  )}
                  {task.actual.done_at && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Completed</span>
                      <span className="font-medium text-gray-900">{formatTime(task.actual.done_at)}</span>
                    </div>
                  )}
                  {task.actual.waiting_before_drive_min !== null && task.actual.waiting_before_drive_min > 0 && (
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <span className="text-gray-600">Wait Before Drive</span>
                      <span className="font-medium text-gray-900">{formatDuration(task.actual.waiting_before_drive_min)}</span>
                    </div>
                  )}
                  {task.actual.waiting_at_location_min !== null && task.actual.waiting_at_location_min > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Wait at Location</span>
                      <span className="font-medium text-gray-900">{formatDuration(task.actual.waiting_at_location_min)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
